import React, { useEffect, useRef } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation } from 'react-router-dom';
import './styles.scss';

import Navbar from './components/navbar/Navbar';
import Footer from './components/footer/Footer';

import SignupPage from './pages/SignupPage';
import LoginPage from './pages/LoginPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import { CurrentUserProvider } from './provider/CurrentUserProvider';
import { AuthProvider } from './provider/AuthProvider';
import useAuth from './hooks/useAuth';

import { DisplayNotificationProvider } from './provider/DisplayNotificationProvider';
import DisplayNotificationManager from './components/displayNotification/DisplayNotificationManager';
import NotFound404Page from './pages/NotFound404Page';
import { SocketProvider } from './provider/SocketProvider';
import BrowseFreelancersPage from './pages/BrowseFreelancersPage';
import MyProjectsPage from './pages/MyProjectsPage';
import UserProfilePage from './pages/UserProfilePage';
import ProjectDetailsPage from './pages/ProjectDetailsPage';

import { ChatProvider } from './provider/ChatProvider';
import ChatPage from './pages/ChatPage';
import { ConfigProvider } from './provider/ConfigProvider';
import { MyTasksProvider } from './provider/MyTasksProvider';
import { MyProjectsProvider } from './provider/MyProjectsProvider';
import { ProjectManagementProvider } from './provider/ProjectManagementProvider';
import { GlobalLoadingProvider } from './provider/GlobalLoadingProvider';
import MainSEO from './Seo';
import EngagementsPage from './pages/EngagementsPage';
import JobsPage from './pages/JobsPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import DashboardPage from './pages/DashboardPage';
import PricingPage from './pages/PricingPage';
import PartnersAndAffiliatesPage from './pages/PartnersAndAffiliatesPage';
import { HelmetProvider } from 'react-helmet-async';
import CreateNewProjectPage from './pages/CreateNewProjectPage';
import { ArielleProvider } from './provider/ArielleProvider';
import useCurrentUser from './hooks/useCurrentUser';
import useRouting from './hooks/useRouting';
import SignupUpdateProfilePage from './pages/SignupUpdateProfilePage';

// Routes
export const LANDING_PAGE_ROUTE: string = '/';
export const TEST_COMPONENTS_ROUTE: string = '/test';
export const SIGNUP_ROUTE: string = '/signup';
export const SIGNUP_UPDATE_PROFILE_ROUTE: string = '/signup-update-profile';
export const LOGIN_ROUTE: string = '/login';
export const RESET_PASSWORD_ROUTE: string = '/reset-password';
export const FORGOT_PASSWORD_ROUTE: string = '/forgot-password';
export const VERIFY_EMAIL_ROUTE: string = '/verify-email';
export const DASHBOARD_ROUTE: string = '/dashboard';
export const CREATE_NEW_PROJECT_ROUTE: string = '/create-new-project';
export const PROJECTS_ROUTE: string = '/projects';
export const USER_PROFILE_ROUTE: string = '/profile';
export const NOT_FOUND_404_ROUTE: string = '/404';
export const BROWSE_FREELANCERS_ROUTE: string = '/browse-talents';
export const MY_PROJECTS_ROUTE: string = '/projects';
export const PROJECT_DETAILS_ROUTE: string = '/project-details';
export const UNASSIGNED_TASKS_ROUTE: string = '/tasks';
export const JOBS_ROUTE: string = '/jobs';
export const CHAT_ROUTE: string = '/chat';
export const ENGAGEMENTS_ROUTE: string = '/engagements';
export const PARTNERS_AND_AFFILIATES_ROUTE: string = '/partners-and-benefits';
export const PRICING_ROUTE: string = '/pricing';

export const ROUTE_TITLE_MAP = {
  [LANDING_PAGE_ROUTE]: 'Gamers Home',
  [TEST_COMPONENTS_ROUTE]: 'Test Components',
  [SIGNUP_ROUTE]: 'Signup',
  [LOGIN_ROUTE]: 'Login',
  [RESET_PASSWORD_ROUTE]: 'Reset Password',
  [FORGOT_PASSWORD_ROUTE]: 'Forgot Password',
  [VERIFY_EMAIL_ROUTE]: 'Verify Email',
  [DASHBOARD_ROUTE]: 'Dashboard',
  [PROJECTS_ROUTE]: 'Projects',
  [USER_PROFILE_ROUTE]: 'User Profile',
  [NOT_FOUND_404_ROUTE]: '404 Not Found',
  [BROWSE_FREELANCERS_ROUTE]: 'Browse Freelancers',
  [MY_PROJECTS_ROUTE]: 'My Projects',
  [PROJECT_DETAILS_ROUTE]: 'Project Details',
  [UNASSIGNED_TASKS_ROUTE]: 'Unassigned Tasks',
  [JOBS_ROUTE]: 'Jobs',
  [CHAT_ROUTE]: 'Chat',
  [ENGAGEMENTS_ROUTE]: 'Engagements',
  [PARTNERS_AND_AFFILIATES_ROUTE]: 'Partners and Affiliates',
  [PRICING_ROUTE]: 'Pricing',
  [CREATE_NEW_PROJECT_ROUTE]: 'Create New Project',
}
function App() {

  return (
    <>
      <HelmetProvider>
        <MainSEO />
        <GlobalLoadingProvider>
          <SocketProvider>
            <DisplayNotificationProvider>
              <ConfigProvider>
                <AuthProvider>
                  <AuthWrapper>
                    <Router>
                      <LoginRedirect>
                        <DisplayNotificationManager />
                        <ArielleProvider>
                          <MyProjectsProvider>
                            <MyTasksProvider>
                              <ProjectManagementProvider>
                                <ChatProvider>
                                  <div className="flex flex-col min-h-screen">

                                    <div className='flex-grow'>
                                      <Navbar />
                                      <Routes>
                                        <Route path={LANDING_PAGE_ROUTE} element={<DashboardPage />} />
                                        <Route path={SIGNUP_ROUTE} element={<SignupPage />} />
                                        <Route path={SIGNUP_UPDATE_PROFILE_ROUTE} element={<SignupUpdateProfilePage />} />
                                        <Route path={LOGIN_ROUTE} element={<LoginPage />} />
                                        <Route path={RESET_PASSWORD_ROUTE} element={<ResetPasswordPage />} />
                                        <Route path={FORGOT_PASSWORD_ROUTE} element={<ForgotPasswordPage />} />
                                        <Route path={VERIFY_EMAIL_ROUTE} element={<VerifyEmailPage />} />

                                        <Route path={CREATE_NEW_PROJECT_ROUTE} element={
                                          <CreateNewProjectPage />} />
                                        <Route path={DASHBOARD_ROUTE} element={<DashboardPage />} />
                                        <Route path={NOT_FOUND_404_ROUTE} element={<NotFound404Page />} />
                                        <Route path={USER_PROFILE_ROUTE} element={<UserProfilePage />} />
                                        <Route path={BROWSE_FREELANCERS_ROUTE} element={<BrowseFreelancersPage />} />
                                        <Route path={MY_PROJECTS_ROUTE} element={<MyProjectsPage />} />
                                        <Route path={PROJECT_DETAILS_ROUTE} element={<ProjectDetailsPage />} />
                                        <Route path={JOBS_ROUTE} element={<JobsPage />} />
                                        <Route path={CHAT_ROUTE} element={<ChatPage />} />
                                        <Route path={ENGAGEMENTS_ROUTE} element={<EngagementsPage />} />
                                        <Route path={PRICING_ROUTE} element={<PricingPage />} />
                                        <Route path={PARTNERS_AND_AFFILIATES_ROUTE} element={<PartnersAndAffiliatesPage />} />
                                      </Routes>
                                    </div>
                                    <Footer />
                                  </div>
                                </ChatProvider>
                              </ProjectManagementProvider>
                            </MyTasksProvider>
                          </MyProjectsProvider>
                        </ArielleProvider>
                      </LoginRedirect>
                    </Router>
                  </AuthWrapper>
                </AuthProvider>
              </ConfigProvider>
            </DisplayNotificationProvider>
          </SocketProvider>
        </GlobalLoadingProvider>
      </HelmetProvider>
    </>
  );
}


// Wrapper to pass authToken to CurrentUserProvider
const AuthWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { authToken } = useAuth(); // Get the authToken from AuthContext
  return <CurrentUserProvider authToken={authToken}>{children}</CurrentUserProvider>;
};
const LoginRedirect: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isLoggedIn, currentUser } = useCurrentUser();
  const { goToSignup, goToLogin, goToDashboard } = useRouting();
  const location = useLocation();
  const prevIsLoggedIn = useRef(isLoggedIn);
  const redirectTimeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    // Don't redirect if we're already on an auth-related page
    const isAuthPage = [
      SIGNUP_ROUTE, 
      LOGIN_ROUTE,
      FORGOT_PASSWORD_ROUTE,
      RESET_PASSWORD_ROUTE,
      VERIFY_EMAIL_ROUTE
    ].includes(location.pathname);

    const isSignupUpdatePage = location.pathname === SIGNUP_UPDATE_PROFILE_ROUTE;

    // Clear any existing timeout
    if (redirectTimeoutRef.current) {
      clearTimeout(redirectTimeoutRef.current);
    }

    // Add a small delay before redirecting to avoid redirect loops during data refetch
    redirectTimeoutRef.current = setTimeout(() => {
      if (!isLoggedIn && !isAuthPage && !isSignupUpdatePage) {
        if (prevIsLoggedIn.current) {
          goToLogin();
        } else {
          goToSignup(); 
        }
      } else if (isLoggedIn && isAuthPage) {
        goToDashboard();
      }
      prevIsLoggedIn.current = isLoggedIn;
    }, 500); // 500ms delay

    return () => {
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current);
      }
    };
  }, [isLoggedIn, location.pathname]);

  return <>{children}</>;
};


export default App;


